<template>
  <div class="card">
    <div class="flex justify-between items-center mb-2">
      <h2 class="text-base font-semibold">Broadsheet Templates</h2>
      <Button v-if="canCreate" label="Create New" icon="pi pi-plus" size="small" @click="$emit('create')" />
    </div>

    <p class="text-sm text-gray-500 mb-2">
      <strong>System Templates</strong> are built-in and cannot be changed. Clone them to create your own <strong>Custom Templates</strong>.
    </p>

    <DataTable :value="templates" :loading="loading" stripedRows showGridlines responsiveLayout="scroll" size="small">
      <Column field="name" header="Name" sortable></Column>
      <Column field="description" header="Description"></Column>
      <Column header="Type">
        <template #body="{ data }">
          <Tag :value="data.school ? 'Custom' : 'System'" :severity="data.school ? 'info' : 'secondary'" />
        </template>
      </Column>
      <Column header="Actions" :style="{ width: '180px' }">
        <template #body="{ data }">
          <div class="flex gap-1">
            <!-- Generate Report -->
            <Button icon="pi pi-file-export" text rounded size="small"
              @click="openReportModal(data)" v-tooltip.top="'Generate Report'" />

            <!-- Edit (Only for Custom) -->
            <Button icon="pi pi-pencil" text rounded severity="secondary" size="small"
              v-if="data.school && canUpdate" @click="$emit('edit', data)" v-tooltip.top="'Edit'" />

            <!-- Clone (For System or Custom) -->
            <Button icon="pi pi-copy" text rounded severity="help" size="small"
              v-if="canCreate" @click="$emit('clone', data)" v-tooltip.top="'Clone'" />

            <!-- Delete (Only for Custom) -->
            <Button icon="pi pi-trash" text rounded severity="danger" size="small"
              v-if="data.school && canDelete" @click="confirmDelete(data)" v-tooltip.top="'Delete'" />
          </div>
        </template>
      </Column>
    </DataTable>

    <ConfirmDialog />

    <!-- Generate Report Modal -->
    <Dialog v-model:visible="reportModalVisible" modal header="Generate Broadsheet Report" :style="{ width: '90vw', maxWidth: '1200px' }" :closable="!reportLoading">
      <div v-if="!reportData">
        <p class="mb-2 text-sm text-gray-600">Select context to generate report for <strong>{{ selectedReportTemplate?.name }}</strong></p>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-2">
          <!-- Tertiary: Programme selector -->
          <div v-if="isTertiary">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Programme</label>
            <ProgrammeSelect v-model="selectedProgrammeId" />
          </div>
          <!-- Class / Level (label flips to "Level" for tertiary via labels.CLASS) -->
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">{{ labels.CLASS }}</label>
            <ClassroomSectionSelect v-model="selectedClassId" :showClassroomsOnly="true" />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Academic Term</label>
            <AcademicCalendarSelect v-model="selectedTermId" size="small" />
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Student Status</label>
            <Select v-model="selectedStudentStatus" :options="studentStatusOptions" optionLabel="label" optionValue="value"
              placeholder="All Students" size="small" class="w-full rounded-lg" showClear />
          </div>
        </div>
        <div class="flex justify-end gap-1.5">
          <Button label="Cancel" text size="small" @click="reportModalVisible = false" />
          <Button label="Download Excel" icon="pi pi-file-excel" outlined size="small" :disabled="!canSubmitReport" :loading="exportLoading" @click="exportToExcel" />
          <Button label="Download PDF" icon="pi pi-file-pdf" severity="danger" outlined size="small" :disabled="!canSubmitReport" :loading="pdfExportLoading" @click="exportToPdf" />
          <Button label="Generate Preview" icon="pi pi-eye" size="small" :disabled="!canSubmitReport" :loading="reportLoading" @click="generateReport" />
        </div>
      </div>

      <!-- Report Results -->
      <div v-else>
        <!-- Meta Header -->
        <div v-if="reportData.meta" class="text-center mb-2 py-2 bg-primary/10 border border-primary/20 rounded-lg">
          <h3 class="text-base font-bold uppercase">
            {{ [reportData.meta.session, reportData.meta.semester].filter(Boolean).join(' — ') }}
          </h3>
          <p class="text-sm text-gray-600 font-semibold uppercase">
            <span v-if="reportData.meta.department_name">{{ reportData.meta.department_name }} — </span>
            <span v-if="reportData.meta.program_name">{{ reportData.meta.program_name }}</span>
            <span v-if="reportData.meta.classroom_name">{{ reportData.meta.classroom_name }}</span>
            <span v-if="reportData.meta.level"> ({{ reportData.meta.level }} Level)</span>
          </p>
        </div>

        <!-- Summary Stats Panel -->
        <div v-if="reportData.summary" class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
          <div v-if="reportData.summary.result_percentage !== undefined" class="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-2 text-center">
            <span class="text-xs uppercase text-primary font-semibold">Class Average</span>
            <div class="text-lg font-bold text-primary">{{ reportData.summary.result_percentage?.toFixed(1) }}%</div>
          </div>
          <div v-if="reportData.summary.total_students !== undefined" class="bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/20 rounded-lg p-2 text-center">
            <span class="text-xs uppercase text-primary font-semibold">Total Students</span>
            <div class="text-lg font-bold text-primary-700">{{ reportData.summary.total_students }}</div>
          </div>
        </div>

        <div class="flex justify-between items-center mb-2">
          <span class="text-xs text-gray-500">Showing {{ reportData.data.length }} students</span>
          <div class="flex gap-1.5">
            <Button label="Back" icon="pi pi-arrow-left" text size="small" @click="reportData = null" />
            <Button label="Print" icon="pi pi-print" size="small" severity="secondary" @click="printReport" />
            <Button label="Export to Excel" icon="pi pi-file-excel" size="small" :loading="exportLoading" @click="exportToExcel" />
            <Button label="Export to PDF" icon="pi pi-file-pdf" severity="danger" size="small" :loading="pdfExportLoading" @click="exportToPdf" />
          </div>
        </div>
        <div class="overflow-x-auto max-h-[60vh]">
          <table class="min-w-full bg-white border border-gray-300 text-xs">
            <thead class="bg-gray-100 text-gray-700 uppercase text-xs font-bold sticky top-0">
              <template v-if="hasGroupedModalHeaders">
                <tr>
                  <th v-for="g in modalHeaderGroups" :key="g.key"
                    :colspan="g.isGroup ? g.colspan : 1"
                    :rowspan="g.isGroup ? 1 : 2"
                    class="py-1 px-2 border-b border-gray-300 whitespace-nowrap"
                    :class="g.isGroup ? 'text-center bg-gray-200 border-l border-r border-gray-300' : 'text-left'">
                    {{ g.label }}
                  </th>
                </tr>
                <tr>
                  <th v-for="sh in modalSubHeaders" :key="sh.key"
                    class="py-1 px-2 border-b border-gray-300 text-left whitespace-nowrap text-[10px]">
                    {{ sh.label }}
                  </th>
                </tr>
              </template>
              <tr v-else>
                <th v-for="header in reportData.headers" :key="header.key" class="py-1 px-2 border-b border-gray-300 text-left whitespace-nowrap">
                  {{ header.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="reportData.data.length === 0">
                <td :colspan="reportData.headers.length" class="py-6 text-center text-gray-500 italic">No students found for the selected criteria.</td>
              </tr>
              <tr v-for="(row, idx) in reportData.data" :key="idx" class="hover:bg-gray-50 border-b border-gray-100">
                <td v-for="header in reportData.headers" :key="header.key" class="py-1 px-2 whitespace-nowrap">
                  <template v-if="(header.type === 'STUDENT_PERCENTAGE' || header.key === 'percentage') && row[header.key] !== '-' && row[header.key] != null">
                    {{ row[header.key] }}%
                  </template>
                  <template v-else-if="header.type === 'STUDENT_POSITION' || header.key === 'position'">
                    <span class="font-semibold text-purple-700">{{ formatPosition(row[header.key]) }}</span>
                  </template>
                  <template v-else>
                    {{ row[header.key] }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Course Legend -->
        <div v-if="reportData.course_legend?.length" class="mt-6">
          <h4 class="text-sm font-bold text-gray-700 mb-2 uppercase">Course Legend</h4>
          <table class="min-w-[400px] bg-white border border-gray-300 text-xs">
            <thead class="bg-gray-100 text-gray-700 uppercase font-bold">
              <tr>
                <th class="py-1.5 px-3 border-b border-gray-300 text-left w-12">SN</th>
                <th class="py-1.5 px-3 border-b border-gray-300 text-left">Course Code</th>
                <th class="py-1.5 px-3 border-b border-gray-300 text-left">Course Title</th>
                <th class="py-1.5 px-3 border-b border-gray-300 text-center w-16">Unit</th>
                <th class="py-1.5 px-3 border-b border-gray-300 text-center w-16">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(course, cidx) in reportData.course_legend" :key="cidx"
                class="border-b border-gray-100 last:border-0">
                <td class="py-1 px-3 text-gray-500">{{ cidx + 1 }}</td>
                <td class="py-1 px-3 font-mono font-medium">{{ course.code }}</td>
                <td class="py-1 px-3">{{ course.title }}</td>
                <td class="py-1 px-3 text-center">{{ course.unit ?? '-' }}</td>
                <td class="py-1 px-3 text-center">{{ course.status === 'C' ? 'Compulsory' : course.status === 'E' ? 'Elective' : course.status || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Grade Legend -->
        <div v-if="reportData.grade_legend?.length" class="mt-4">
          <h4 class="text-sm font-bold text-gray-700 mb-2 uppercase">Grading Key</h4>
          <table class="min-w-[300px] bg-white border border-gray-300 text-xs">
            <thead class="bg-gray-100 text-gray-700 uppercase font-bold">
              <tr>
                <th class="py-1.5 px-3 border-b border-gray-300 text-left">Grade</th>
                <th class="py-1.5 px-3 border-b border-gray-300 text-left">Score Range</th>
                <th class="py-1.5 px-3 border-b border-gray-300 text-center">Point</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in reportData.grade_legend" :key="g.grade"
                class="border-b border-gray-100 last:border-0">
                <td class="py-1 px-3 font-bold">{{ g.grade }}</td>
                <td class="py-1 px-3">{{ g.range }}</td>
                <td class="py-1 px-3 text-center">{{ g.point }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import {
  useGetBroadsheetTemplatesQuery,
  useDeleteBroadsheetTemplateMutation,
  useGetBroadsheetLazyQuery,
  useGetBroadsheetExcelUrlLazyQuery,
} from '@/types/vue-apollo-helpers';
import { useLazyQuery } from '@vue/apollo-composable';
import { GET_BROADSHEET_PDF_URL } from '@/graphql/Queries/broadsheet';
import type { BroadsheetTemplate, BroadsheetReport } from '@/types/composition-functions';
import type { GetBroadsheetExcelUrlQuery, GetBroadsheetQueryVariables, GetBroadsheetExcelUrlQueryVariables } from '@/types/operations';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';

const emit = defineEmits(['create', 'edit', 'clone']);

const confirm = useConfirm();
const toast = useToast();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);
const { isTertiary } = useSchoolFeatures();
const { labels } = useSchoolLabels();
const { levelForId } = useTertiaryLevels();

const { result, loading, refetch } = useGetBroadsheetTemplatesQuery({ fetchPolicy: 'network-only' });
const templates = computed(() => result.value?.broadsheetTemplates?.data || []);

const { mutate: deleteTemplate } = useDeleteBroadsheetTemplateMutation();

const canCreate = computed(() => can(currentUser.value, AppPermissions.Results.Actions.CreateBroadsheet, AppPermissions.Results.Resource));
const canUpdate = computed(() => can(currentUser.value, AppPermissions.Results.Actions.UpdateBroadsheet, AppPermissions.Results.Resource));
const canDelete = computed(() => can(currentUser.value, AppPermissions.Results.Actions.DeleteBroadsheet, AppPermissions.Results.Resource));

const confirmDelete = (template: BroadsheetTemplate) => {
  confirm.require({
    message: `Are you sure you want to delete "${template.name}"?`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        await deleteTemplate({ id: template.id });
        toast.add({ severity: 'success', summary: 'Deleted', detail: 'Template deleted', life: 3000 });
        refetch();
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'An unexpected error occurred';
        toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
      }
    },
  });
};

// --- Report Generation Logic ---

const reportModalVisible = ref(false);
const selectedReportTemplate = ref<BroadsheetTemplate | null>(null);
const selectedClassId = ref();
const selectedTermId = ref();
const selectedProgrammeId = ref();
const selectedStudentStatus = ref<string | null>(null);
const reportData = ref<BroadsheetReport | null>(null);

const studentStatusOptions = [
  { label: 'Enrolled', value: 'enrolled' },
  { label: 'Graduated', value: 'graduated' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Transferred', value: 'transferred' },
  { label: 'Promoted', value: 'promoted' },
];
const reportLoading = ref(false);
const exportLoading = ref(false);

const canSubmitReport = computed(() =>
  isTertiary.value
    ? !!(selectedProgrammeId.value && selectedClassId.value && selectedTermId.value)
    : !!(selectedClassId.value && selectedTermId.value),
);

const getReportVariables = (): GetBroadsheetQueryVariables | GetBroadsheetExcelUrlQueryVariables => {
  const templateId = selectedReportTemplate.value!.id;
  const base: GetBroadsheetQueryVariables = isTertiary.value
    ? { calId: selectedTermId.value, templateId, programmeId: selectedProgrammeId.value, level: levelForId(selectedClassId.value) ?? undefined }
    : { classId: selectedClassId.value, calId: selectedTermId.value, templateId };
  if (selectedStudentStatus.value) {
    base.student_status = selectedStudentStatus.value;
  }
  return base;
};

const openReportModal = (template: BroadsheetTemplate) => {
  selectedReportTemplate.value = template;
  selectedClassId.value = null;
  selectedTermId.value = null;
  selectedProgrammeId.value = null;
  selectedStudentStatus.value = null;
  reportData.value = null;
  reportModalVisible.value = true;
};

const { load: fetchReport, refetch: refetchReport, result: reportResult } = useGetBroadsheetLazyQuery();
const { load: fetchExcelUrl, refetch: refetchExcelUrl, result: excelResult } = useGetBroadsheetExcelUrlLazyQuery();

const generateReport = async() => {
  if (!canSubmitReport.value || !selectedReportTemplate.value) return;

  reportLoading.value = true;
  try {
    const vars = getReportVariables();
    const loadResult = await fetchReport(null, vars);
    if (loadResult) {
      reportData.value = loadResult.broadsheetReport;
    } else {
      const refetchResult = await refetchReport(vars);
      reportData.value = refetchResult?.data?.broadsheetReport ?? null;
    }
    if (!reportData.value) {
      toast.add({ severity: 'warn', summary: 'No Data', detail: 'No report data returned', life: 3000 });
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'An unexpected error occurred';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    reportLoading.value = false;
  }
};

// Format position as ordinal (1st, 2nd, 3rd, etc.)
const formatPosition = (pos: number | string) => {
  const n = typeof pos === 'string' ? parseInt(pos) : pos;
  if (isNaN(n)) return pos;
  const s: string[] = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  const suffix = s[(v - 20) % 10] ?? s[v] ?? 'th';
  return n + suffix;
};

// Grouped header logic for modal preview
const hasGroupedModalHeaders = computed(() =>
  reportData.value?.headers?.some((h: Record<string, unknown>) => h.group) ?? false,
);

const modalHeaderGroups = computed(() => {
  if (!reportData.value?.headers) return [];
  const groups: Array<{ label: string; key: string; colspan: number; isGroup: boolean }> = [];
  const headers = reportData.value.headers;
  let i = 0;
  while (i < headers.length) {
    const h = headers[i];
    if (h.group) {
      let count = 1;
      while (i + count < headers.length && headers[i + count].group === h.group) count++;
      groups.push({ label: h.group, key: `group_${i}`, colspan: count, isGroup: true });
      i += count;
    } else {
      groups.push({ label: h.label, key: h.key, colspan: 1, isGroup: false });
      i++;
    }
  }
  return groups;
});

const modalSubHeaders = computed(() =>
  reportData.value?.headers?.filter((h: Record<string, unknown>) => h.group) ?? [],
);

const exportToExcel = async() => {
  if (!canSubmitReport.value || !selectedReportTemplate.value) return;

  exportLoading.value = true;
  try {
    const vars = getReportVariables();
    const loadResult = await fetchExcelUrl(null, vars);
    let exportRecord: GetBroadsheetExcelUrlQuery['broadsheetExcelUrl'] | undefined;
    if (loadResult) {
      exportRecord = loadResult.broadsheetExcelUrl;
    } else {
      const refetchResult = await refetchExcelUrl(vars);
      exportRecord = refetchResult?.data?.broadsheetExcelUrl;
    }

    if (exportRecord?.id) {
      toast.add({ severity: 'success', summary: 'Export Started', detail: 'Your broadsheet is being generated. Check the Downloads icon in the top bar for progress.', life: 5000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Could not initiate broadsheet export.', life: 3000 });
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'An unexpected error occurred';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    exportLoading.value = false;
  }
};

// PDF export (backend broadsheetPdfUrl; raw query — no generated hook yet).
const pdfExportLoading = ref(false);
const { load: fetchPdfUrl, refetch: refetchPdfUrl } = useLazyQuery(GET_BROADSHEET_PDF_URL);

const exportToPdf = async() => {
  if (!canSubmitReport.value || !selectedReportTemplate.value) return;

  pdfExportLoading.value = true;
  try {
    const vars = getReportVariables();
    const loadResult = await fetchPdfUrl(null, vars);
    const record = loadResult ? loadResult.broadsheetPdfUrl : (await refetchPdfUrl(vars))?.data?.broadsheetPdfUrl;

    if (record?.id) {
      toast.add({ severity: 'success', summary: 'Export Started', detail: 'Your broadsheet PDF is being generated. Check the Downloads icon in the top bar for progress.', life: 5000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Could not initiate broadsheet PDF export.', life: 3000 });
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'An unexpected error occurred';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    pdfExportLoading.value = false;
  }
};

const printReport = () => {
  if (!reportData.value) return;

  const bd = reportData.value;
  const allHeaders = bd.headers as Array<Record<string, unknown>>;

  const isDataCol = (h: Record<string, unknown>) =>
    h.type === 'DYNAMIC_SUBJECT_SCORE' ||
    h.type === 'STUDENT_PERCENTAGE' ||
    h.type === 'STUDENT_POSITION' ||
    h.key === 'percentage' ||
    h.key === 'position';

  let splitIdx = allHeaders.findIndex(h => isDataCol(h));
  if (splitIdx === -1) splitIdx = allHeaders.length;

  const studentHdrs = allHeaders.slice(0, splitIdx);
  const dataHdrs = allHeaders.slice(splitIdx);

  const MAX_DATA_COLS_PER_PAGE = 20;

  const esc = (v: unknown) => {
    const s = String(v ?? '-');
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  const fmtPos = (pos: unknown) => {
    const n = typeof pos === 'string' ? parseInt(pos) : (pos as number);
    if (isNaN(n)) return esc(pos);
    const sfx = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    const suffix = sfx[(v - 20) % 10] ?? sfx[v] ?? 'th';
    return n + suffix;
  };

  let metaHtml = '';
  if (bd.meta) {
    const m = bd.meta as Record<string, unknown>;
    const session = m.session ? esc(m.session) : '';
    const semester = m.semester ? esc(m.semester) : '';
    const title = [session, semester].filter(Boolean).join(' &mdash; ');
    const dept = m.department_name ? esc(m.department_name) + ' &mdash; ' : '';
    const prog = m.program_name ? esc(m.program_name) : '';
    const cls = m.classroom_name ? esc(m.classroom_name) : '';
    const lvl = m.level ? ' (' + esc(m.level) + ' Level)' : '';
    metaHtml = `<div class="print-meta">
      <h3>${title}</h3>
      <p>${dept}${prog}${cls}${lvl}</p>
    </div>`;
  }

  const buildColgroup = (
    sHdrs: typeof studentHdrs,
    dHdrs: typeof dataHdrs,
  ) => {
    const studentWidths = sHdrs.map(h => {
      if (h.key === 'sn') return 2.5;
      const k = String(h.key);
      if (k.includes('matric') || k.includes('admission')) return 11;
      if (k.includes('name')) return 15;
      return 8;
    });
    const studentTotal = studentWidths.reduce((a, b) => a + b, 0);

    const summaryWidths: (number | null)[] = dHdrs.map(h => {
      if (h.type === 'STUDENT_PERCENTAGE' || h.key === 'percentage') return 5;
      if (h.type === 'STUDENT_POSITION' || h.key === 'position') return 5;
      return null;
    });
    const fixedTotal = summaryWidths
      .filter((w): w is number => w !== null)
      .reduce((a, b) => a + b, 0);
    const courseCount = summaryWidths.filter(w => w === null).length;
    const coursePct = 100 - studentTotal - fixedTotal;
    const courseColPct = courseCount > 0 ? coursePct / courseCount : 0;

    let cols = studentWidths.map(w => `<col style="width:${w}%">`).join('');
    cols += dHdrs.map((_h, i) => {
      const sw = summaryWidths[i] ?? null;
      const w = sw !== null ? sw : courseColPct;
      return `<col style="width:${w.toFixed(2)}%">`;
    }).join('');

    return `<colgroup>${cols}</colgroup>`;
  };

  const studentCellsFn = (row: Record<string, unknown>, idx: number) =>
    studentHdrs.map(h => {
      const val = h.key === 'sn' ? idx + 1 : row[h.key as string];
      return `<td class="td-student">${esc(val)}</td>`;
    }).join('');

  const studentHeaderCells = studentHdrs
    .map(h => `<th class="th-student">${esc(h.label)}</th>`)
    .join('');

  const pageChunks: (typeof dataHdrs)[] = [];
  if (dataHdrs.length > 0) {
    for (let i = 0; i < dataHdrs.length; i += MAX_DATA_COLS_PER_PAGE) {
      pageChunks.push(dataHdrs.slice(i, i + MAX_DATA_COLS_PER_PAGE));
    }
  } else {
    pageChunks.push([]);
  }

  const totalPages = pageChunks.length;
  const hasGroups = dataHdrs.some(h => h.group);

  const timestamp = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  let tablesHtml = '';

  pageChunks.forEach((chunk, chunkIdx) => {
    const colgroup = buildColgroup(studentHdrs, chunk);
    const pageLabel = totalPages > 1
      ? ` (Page ${chunkIdx + 1} of ${totalPages})`
      : '';

    let theadContent = '';

    if (hasGroups && chunk.some(h => h.group)) {
      let groupRow = '<th colspan="' + studentHdrs.length
        + '" rowspan="2" class="th-group bg-student">'
        + 'Student Info' + pageLabel + '</th>';
      let detailRow = '';

      let i = 0;
      while (i < chunk.length) {
        const h = chunk[i]!;
        if (h.group) {
          let count = 1;
          while (
            i + count < chunk.length &&
            chunk[i + count]?.group === h.group
          ) count++;
          groupRow += `<th colspan="${count}" class="th-group bg-course">`
            + `${esc(h.group)}</th>`;
          for (let j = i; j < i + count; j++) {
            detailRow += `<th class="th-course">${esc(chunk[j]!.label)}</th>`;
          }
          i += count;
        } else {
          groupRow += '<th rowspan="2" class="th-summary">'
            + `${esc(h.label)}</th>`;
          i++;
        }
      }

      theadContent = `<tr>${groupRow}</tr><tr>${detailRow}</tr>`;
    } else {
      const dataHeaderCells = chunk.map(h => {
        if (h.type === 'DYNAMIC_SUBJECT_SCORE' && h.meta) {
          const meta = h.meta as Record<string, unknown>;
          let sub = '';
          if (meta.unit !== undefined && meta.unit !== null) sub += meta.unit;
          if (meta.status) sub += (sub ? ' &middot; ' : '') + esc(meta.status);
          return `<th class="th-course"><div>${esc(h.label)}</div>`
            + (sub ? `<div class="th-sub">${sub}</div>` : '') + '</th>';
        }
        return `<th class="th-summary">${esc(h.label)}</th>`;
      }).join('');

      if (totalPages > 1) {
        const grpRow = '<tr>'
          + `<th colspan="${studentHdrs.length}" class="th-group bg-student">Student Info${pageLabel}</th>`
          + `<th colspan="${chunk.length}" class="th-group bg-course">Scores</th></tr>`;
        theadContent = `${grpRow}<tr>${studentHeaderCells}${dataHeaderCells}</tr>`;
      } else {
        theadContent = `<tr>${studentHeaderCells}${dataHeaderCells}</tr>`;
      }
    }

    const dataRows = (bd.data as Record<string, unknown>[]).map((row, idx) => {
      const sCells = studentCellsFn(row, idx);
      const dCells = chunk.map(h => {
        const val = row[h.key as string];
        if (
          (h.type === 'STUDENT_PERCENTAGE' || h.key === 'percentage') &&
          val !== null && val !== undefined && val !== '-'
        ) {
          return `<td class="td-summary">${val}%</td>`;
        }
        if (h.type === 'STUDENT_POSITION' || h.key === 'position') {
          return `<td class="td-summary">${fmtPos(val)}</td>`;
        }
        return `<td class="td-score">${esc(val)}</td>`;
      }).join('');
      return `<tr>${sCells}${dCells}</tr>`;
    }).join('\n');

    tablesHtml += `
      ${metaHtml}
      <table>${colgroup}
        <thead>${theadContent}</thead>
        <tbody>${dataRows}</tbody>
      </table>
      <div class="print-footer">Generated on ${timestamp}</div>
      ${chunkIdx < totalPages - 1 ? '<div class="page-break"></div>' : ''}`;
  });

  const buildLegendHtml = () => {
    let html = '';
    const cl = bd.course_legend as Array<Record<string, unknown>> | undefined;
    if (cl?.length) {
      const rows = cl.map((c, i) => {
        const st = c.status === 'C' ? 'Compulsory'
          : c.status === 'E' ? 'Elective' : c.status || '-';
        return `<tr><td>${i + 1}</td><td class="font-mono">${esc(c.code)}</td>`
          + `<td>${esc(c.title)}</td><td class="text-center">${c.unit ?? '-'}</td>`
          + `<td class="text-center">${st}</td></tr>`;
      }).join('');
      html += `<div class="print-legend">
        <div class="legend-title">Course Legend</div>
        <table class="legend-table">
          <thead><tr><th>SN</th><th>Code</th><th>Title</th><th>Unit</th><th>Status</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    }
    const gl = bd.grade_legend as Array<Record<string, unknown>> | undefined;
    if (gl?.length) {
      const rows = gl.map(g =>
        `<tr><td class="font-bold">${esc(g.grade)}</td>`
        + `<td>${esc(g.range)}</td>`
        + `<td class="text-center">${esc(g.point)}</td></tr>`,
      ).join('');
      html += `<div class="print-legend">
        <div class="legend-title">Grading Key</div>
        <table class="legend-table">
          <thead><tr><th>Grade</th><th>Score Range</th><th>Point</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    }
    return html;
  };

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (!printWindow) return;

  printWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Broadsheet Report</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; background: white; }

  .print-meta { text-align: center; padding: 6px 0; margin-bottom: 4px; }
  .print-meta h3 { font-size: 13pt; font-weight: 700; text-transform: uppercase; }
  .print-meta p { font-size: 9pt; font-weight: 600; text-transform: uppercase; color: #4b5563; margin-top: 2px; }

  .page-break { page-break-after: always; height: 0; }

  table { border-collapse: collapse; width: 100%; table-layout: fixed; }
  thead { display: table-header-group; background: #f0f0f0;
          -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  tbody { display: table-row-group; }
  tr { page-break-inside: avoid; }

  .th-group { padding: 3px 2px; font-size: 7pt; font-weight: 700;
              border: 0.5px solid #999; text-transform: uppercase; text-align: center; }
  .bg-student { background: #e5e7eb !important;
                -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .bg-course { background: #dbeafe !important;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  th { font-weight: 700; padding: 2px 1px; font-size: 6pt;
       border: 0.5px solid #999; text-transform: uppercase; text-align: center;
       white-space: normal; word-break: break-word; vertical-align: bottom; }
  .th-student { text-align: left; font-size: 8pt; padding: 3px 4px; white-space: nowrap; }
  .th-course { font-size: 6.5pt; line-height: 1.2; }
  .th-sub { font-size: 5.5pt; color: #888; font-weight: 600; }
  .th-summary { font-size: 7pt; padding: 2px 3px; white-space: nowrap; }

  td { padding: 2px 2px; font-size: 8pt; font-weight: 600;
       border: 0.5px solid #bbb; white-space: nowrap; text-align: center; }
  .td-student { text-align: left; padding: 2px 4px; overflow: hidden; text-overflow: ellipsis; }
  .td-score { font-size: 8pt; }
  .td-summary { font-size: 8pt; font-weight: 700; }

  .print-legend { margin-top: 10px; page-break-inside: avoid;
                   border: 1px solid #d1d5db; border-radius: 6px; overflow: hidden; }
  .legend-title { background: #f3f4f6; padding: 6px; font-weight: 700; text-align: center;
                   font-size: 9pt; border-bottom: 1px solid #d1d5db; text-transform: uppercase;
                   -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .legend-table { table-layout: auto !important; width: 100%; }
  .legend-table th, .legend-table td { font-size: 8pt; padding: 3px 6px;
                                        text-align: left; border: 0.5px solid #ddd; }
  .legend-table thead { background: #f9fafb !important;
                         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .font-mono { font-family: monospace; }
  .font-bold { font-weight: 700; }
  .text-center { text-align: center; }

  .print-footer {
    margin-top: 12px; text-align: center; font-size: 7pt; color: #888;
    padding: 4px 0; border-top: 0.5px solid #ccc;
  }
</style>
</head><body>
${tablesHtml}${buildLegendHtml()}</body></html>`);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 300);
};
</script>
